/** The signed-in user (GET /api/me), cached for the auth gate and shared by every page. */
import { useSyncExternalStore } from "react";
import type { MeView } from "../../shared/contracts";
import { api, ApiError } from "./api";
import { agentSocket } from "./socket";
import { clearResources } from "./store";

/** undefined = not checked yet, null = signed out. */
let me: MeView | null | undefined = undefined;
let inflight: Promise<MeView | null> | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const fn of [...listeners]) fn();
}

export function setMe(next: MeView | null): void {
  me = next;
  emit();
}

export function currentMe(): MeView | null | undefined {
  return me;
}

/** Resolves the session (cached). Network or server errors reject; a 401 resolves to null. */
export function loadMe(force = false): Promise<MeView | null> {
  if (!force && me !== undefined) return Promise.resolve(me);
  if (inflight) return inflight;
  inflight = api("GET /api/me", { redirectOn401: false })
    .then(
      (value) => {
        setMe(value);
        return value;
      },
      (err: unknown) => {
        if (err instanceof ApiError && err.status === 401) {
          setMe(null);
          return null;
        }
        throw err;
      },
    )
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

export function useMe(): MeView | null | undefined {
  return useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    () => me,
    () => me,
  );
}

/** Drops everything tied to the session (cache, realtime socket). */
export function resetSession(): void {
  agentSocket.stop();
  clearResources();
  setMe(null);
}

export async function logout(): Promise<void> {
  try {
    await api("POST /api/auth/logout", { body: {} });
  } catch {
    // The cookie may already be gone; signing out locally is what matters.
  } finally {
    resetSession();
  }
}
